import KeyFigureSeriesSpecification from "./KeyFigureSeriesSpecification.js";
import Model from "../../Model.js";
import NestedMap from "../../../core/NestedMap.js";
import PeriodRollingSeriesSpecification from "./PeriodRollingSeriesSpecification.js";
import RowSeriesSpecification from "./RowSeriesSpecification.js";
import seriesSpecificationFactory from "./seriesSpecificationFactory.js";

/**
 * Represents the data sent by the server as result of a
 * `ForecastComputationRequest`.
 *
 * @param {object[]} series - The computed data for every row. Each
 *   element of the array is an object {id, values}, `id` being the
 *   row id sent from `ForecastComputationResult` and `values` being
 *   an array of numbers.
 * @param {KeyFigure[]} keyFigures - List of all available keyFigures
 */
export default class ForecastComputationResult extends Model {
	constructor({ series, keyFigures }) {
		super(...arguments);

		this._keyFigures = keyFigures;
		this._valuesByKey = this._makeValuesByKeyMap({ series });
	}

	getKeyFigureValues({ keyFigureId, report, operationType }) {
		const keyFigure = this._keyFigures.find(
			(kpi) => kpi.getId() === keyFigureId,
		);

		const key = KeyFigureSeriesSpecification.makeKeyFromModel({
			keyFigure,
			report,
			operationType,
		});

		const values = this._getSeriesSpecification(key)?.getKeyFigureValues();

		return values || [];
	}

	getRollingPeriodValues({ id, operationType }) {
		const key = PeriodRollingSeriesSpecification.makeKeyFromModel({
			id,
			operationType,
		});

		return this._getSeriesSpecification(key);
	}

	setDataIntoReport(report) {
		const columns = report.getColumns();

		this._getRows({ report }).forEach((row) => {
			const operationType = report.getOperationType({ row });

			const key = RowSeriesSpecification.makeKeyFromModel({
				row,
				operationType,
			});

			this._getSeriesSpecification(key)?.setDataIntoRow({ row, columns });
		});
	}

	_getSeriesSpecification(key) {
		return this._valuesByKey.get(this._makeArray(key));
	}

	_makeArray(key) {
		return [key.id, key.type, key.operationType];
	}

	_getRows({ report }) {
		return report.getAllRows().filter((row) => row.canBeCalculated());
	}

	_makeValuesByKeyMap({ series }) {
		return new NestedMap({
			pairs: series.map((data) => this._makeNestedMapPairs(data)),
		});
	}

	_makeNestedMapPairs(data) {
		const seriesSpecification = seriesSpecificationFactory.materialize(
			data,
			{
				keyFigures: this._keyFigures,
			},
		);

		return [
			this._makeArray(seriesSpecification.getKey()),
			seriesSpecification,
		];
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.keyFigures = context.keyFigures;
	}
}
